import { DeleteOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, message, Modal, Tag } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CiCirclePlus } from "react-icons/ci";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { getBusinesses, getStampcards } from "../actions/acc_actions";
import { deleteStampcard } from "../actions/stampcard_action";
import FlipCard from "../components/FlipCard";
import ContentLayout from "../components/layout/ContentLayout";
import Loading from "../utils/Loading";

export default function StampCard() {
  const { t } = useTranslation();
  const accUrn = useSelector((state) => state.account.urn);
  const businessUrn = useSelector((state) => state.account.businessUrn);

  const {
    isLoading: stampLoading,
    refetch,
    data: stampcardsData,
  } = useQuery({
    queryKey: ["stampcards", accUrn],
    queryFn: () => getStampcards(businessUrn, accUrn),
    enabled: !!accUrn && !!businessUrn,
  });

  const { isLoading: businessLoading, data: businessesData } = useQuery({
    queryKey: ["userBusinesses", accUrn],
    queryFn: () => getBusinesses(accUrn),
    enabled: !!accUrn,
  });

  const isLoading = businessLoading || stampLoading;

  const business = businessesData?.data?.at(-1);

  // State for delete modal and reason input
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [cardToDelete, setCardToDelete] = useState(null);

  // Show modal and set card urn to delete
  const showDeleteModal = (cardUrn) => {
    setCardToDelete(cardUrn);
    setDeleteReason("");
    setDeleteModalVisible(true);
  };

  // Confirm deletion with reason
  const handleDeleteConfirm = () => {
    if (!deleteReason.trim()) {
      message.error(t("Please provide a reason for deletion."));
      return;
    }

    deleteStampcard({
      loyalty_scheme_urn: cardToDelete,
      account_urn: accUrn,
      reason: deleteReason,
    })
      .then(() => {
        refetch();
        message.success(
          t("The deletion request is now pending for confirmation."),
        );
        setDeleteModalVisible(false);
        setCardToDelete(null);
      })
      .catch(() => {
        message.error(t("Something went wrong!!"));
      });
  };

  // Cancel deletion modal
  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setCardToDelete(null);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ContentLayout
      header={
        !isLoading ? (
          <>
            <h1>
              {t("Welcome to")} <br /> <span>{t("Stamp Card Dashboard")}</span>
            </h1>{" "}
            <h6 className="content_layout-subtitle">
              {t(
                "* There will be an additional cost for each extra stampcard.",
              )}
            </h6>
          </>
        ) : (
          <div className="screen-notification">
            <img src="/assets/images/bg/shop.png" alt="" />
            <h2 className="active">{t("Start Your Online Shop")}</h2>
            <h5>
              {t(
                "Don't miss out on boosting your sales and customer loyalty! Purchase gift cards through our app now.",
              )}
            </h5>
            <Link className="btn primary" to="add">
              {t("Add Your First Voucher")}
            </Link>
          </div>
        )
      }
    >
      {!isLoading && (
        <div className="outlet-cards">
          <Link to="add" className="outlet-add btn">
            <CiCirclePlus size={48} />
            <div className="outlet-add-content">
              <p>{t("Add New")}</p>
              <h4 className="active">{t("Stamp Card")}</h4>
            </div>
          </Link>

          <div className="cards-container">
            {stampcardsData?.data?.map((item, index) => {
              const isPortal = item.rules?.by_portal;

              return (
                <div
                  className={`stampcard-conatiner ${
                    item.hasDeletionRequest ? "pending-deletion" : ""
                  }`}
                  key={index}
                >
                  <div className="status-badge">
                    <Tag
                      color={
                        item.approve_status === "ACCEPTED"
                          ? "green"
                          : item.approve_status === "REJECTED"
                            ? "red"
                            : "orange"
                      }
                    >
                      {t(item.approve_status || "UNKNOWN")}
                    </Tag>
                  </div>
                  {isPortal ? (
                    <FlipCard
                      className="flipcard"
                      business={business}
                      item={item}
                    />
                  ) : (
                    <div
                      style={{ background: "#e9e9e9ff", position: "relative" }}
                      className="card-container"
                    >
                      <h3
                        style={{
                          position: "absolute",
                          top: "40%",
                          left: "10%",
                          width: "80%",
                          textAlign: "center",
                        }}
                        className="card-title"
                      >
                        {item.name || t("Unnamed")}
                      </h3>
                    </div>
                  )}

                  {/* 🔹 Status Tag */}

                  <Button
                    type="primary"
                    className="delete-btn"
                    icon={<DeleteOutlined />}
                    onClick={() => showDeleteModal(item.urn)}
                    danger
                    disabled={item.hasDeletionRequest}
                  >
                    {t("Delete")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Delete Reason Modal */}
      <Modal
        title={t("Reason for Deleting the Card")}
        visible={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText={t("Delete")}
        okButtonProps={{ danger: true }}
      >
        <Form layout="vertical">
          <Form.Item
            label={t("Please provide a reason")}
            required
            validateStatus={deleteReason.trim() ? "success" : "error"}
            help={!deleteReason.trim() ? t("Reason is required.") : ""}
          >
            <Input.TextArea
              rows={4}
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t("Type your reason for deletion here...")}
            />
          </Form.Item>
        </Form>
      </Modal>
    </ContentLayout>
  );
}
